import { useEffect, useState, useRef } from 'react'
import { ProfileLayout } from '@/components/layout/ProfileLayout'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import {
  subscriptionsApi,
  type SubscriptionPlan,
  type UserSubscription,
} from '@/api/subscriptions'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  Divider,
  Skeleton,
  FormControlLabel,
  Checkbox,
  Link as MuiLink,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { useBalanceStore } from '@/stores/balanceStore'
import { formatMoney } from '@/components/billing/formatMoney'
import { ROUTES } from '@/utils/constants'
import { getErrorMessage } from '@/utils/error'

interface InsufficientBalanceInfo {
  required: string
  available: string
}

export function SubscriptionPlansPage() {
  const { t } = useTranslation()
  const user = useAuthStore((state) => state.user)
  const balance = useBalanceStore((s) => s.balance)
  const balanceCurrency = useBalanceStore((s) => s.currency)
  const fetchBalance = useBalanceStore((s) => s.fetchBalance)

  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [subscriptionsList, setSubscriptionsList] = useState<UserSubscription[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [insufficient, setInsufficient] = useState<InsufficientBalanceInfo | null>(null)
  const [isSubscribing, setIsSubscribing] = useState(false)
  const [startAfterCurrentForPlanId, setStartAfterCurrentForPlanId] = useState<number | null>(null)
  const hasLoadedRef = useRef(false)

  const canManageSubscription = user?.roles?.some((r) => r.slug === 'teacher')

  useEffect(() => {
    if (canManageSubscription) {
      void fetchBalance().catch((err) => {
        setError(getErrorMessage(err) || t('common.error'))
      })
    }
  }, [canManageSubscription, fetchBalance, t])

  const hasPendingSubscription = subscriptionsList.some(
    (s) => s.payment?.status === 'pending',
  )
  const currentSubscription =
    subscriptionsList.find((s) => s.is_active) ??
    subscriptionsList.find((s) => s.payment?.status === 'pending') ??
    null

  useEffect(() => {
    if (!canManageSubscription) return
    if (hasLoadedRef.current) return
    hasLoadedRef.current = true
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageSubscription])

  const loadData = async () => {
    if (!canManageSubscription) return
    try {
      setIsLoading(true)
      setError(null)
      const [plansData, list] = await Promise.all([
        subscriptionsApi.getPlans(),
        subscriptionsApi.getSubscriptionsList(),
      ])
      setPlans(
        Array.isArray(plansData)
          ? plansData.filter((p: SubscriptionPlan) => p.visible_in_catalog !== false)
          : [],
      )
      setSubscriptionsList(Array.isArray(list) ? list : [])
    } catch (err: unknown) {
      setError(getErrorMessage(err) || t('subscription.loadError'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubscribe = async (planId: number) => {
    try {
      setIsSubscribing(true)
      setError(null)
      setInsufficient(null)
      const activeExpires = subscriptionsList.find((s) => s.is_active)?.expires_at
      const startsAt =
        startAfterCurrentForPlanId === planId && activeExpires
          ? activeExpires.slice(0, 10)
          : undefined
      await subscriptionsApi.subscribe(planId, startsAt)
      await loadData()
      void fetchBalance().catch(() => undefined)
      setStartAfterCurrentForPlanId(null)
    } catch (err: unknown) {
      const e = err as {
        response?: {
          data?: { message?: string; data?: { required?: string; available?: string } }
          status?: number
        }
        message?: string
      }
      const details = e.response?.data?.data
      if (e.response?.status === 422 && details?.required && details?.available) {
        setInsufficient({ required: details.required, available: details.available })
      } else {
        setError(getErrorMessage(err) || t('subscription.subscribeError'))
      }
    } finally {
      setIsSubscribing(false)
    }
  }

  const isPlanActive = (planId: number) =>
    currentSubscription?.plan?.id === planId && currentSubscription?.is_active === true

  if (!canManageSubscription) {
    return (
      <ProfileLayout>
        <Box sx={{ p: 3 }}>
          <Alert severity="info">{t('subscription.studentInfo')}</Alert>
        </Box>
      </ProfileLayout>
    )
  }

  return (
    <ProfileLayout>
      <Box sx={{ p: 3, pt: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('subscription.availablePlans')}
          </Typography>
          <PageHelpPopover pageKey="profile-plans" />
          <Box sx={{ flex: 1 }} />
          {balance !== null && (
            <Chip
              component={RouterLink}
              to={ROUTES.PROFILE_BALANCE}
              clickable
              label={`${t('tts.balance', 'Баланс')}: ${formatMoney(balance, balanceCurrency)}`}
            />
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {insufficient && (
          <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setInsufficient(null)}>
            {t('subscription.insufficientBalance', {
              required: formatMoney(insufficient.required, balanceCurrency),
              available: formatMoney(insufficient.available, balanceCurrency),
            })}{' '}
            <MuiLink component={RouterLink} to={ROUTES.PROFILE_BALANCE}>
              {t('tts.topupLink', 'Пополнить баланс')}
            </MuiLink>
          </Alert>
        )}

        {hasPendingSubscription && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('subscription.hasPendingSubscription')}
          </Alert>
        )}

        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 3,
              flexWrap: 'wrap',
            }}
          >
            {[1, 2, 3].map((index) => (
              <Box
                key={index}
                sx={{
                  flex: { xs: '1 1 100%', md: '1 1 calc(33.333% - 16px)' },
                  minWidth: 0,
                }}
              >
                <Card>
                  <CardContent>
                    <Skeleton variant="text" width="60%" height={32} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="80%" height={20} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="50%" height={40} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="70%" height={20} sx={{ mb: 3 }} />
                    <Divider sx={{ my: 2 }} />
                    <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
                    {[1, 2, 3, 4].map((item) => (
                      <Skeleton
                        key={item}
                        variant="text"
                        width="90%"
                        height={20}
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                    <Box sx={{ mt: 3 }}>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={36}
                        sx={{ borderRadius: 1 }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 3,
              flexWrap: 'wrap',
            }}
          >
            {plans.map((plan) => {
              const isActive = isPlanActive(plan.id)
              const isCurrentPlan = currentSubscription?.plan?.id === plan.id

              return (
                <Box
                  key={plan.id}
                  sx={{
                    flex: { xs: '1 1 100%', md: '1 1 calc(33.333% - 16px)' },
                    minWidth: 0,
                  }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      border: isCurrentPlan ? '2px solid' : '1px solid',
                      borderColor: isCurrentPlan ? 'primary.main' : 'divider',
                      position: 'relative',
                    }}
                  >
                    {isCurrentPlan && (
                      <Chip
                        label={t('subscription.currentPlan')}
                        color="primary"
                        sx={{ position: 'absolute', top: 16, right: 16 }}
                      />
                    )}
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h5" component="h3" gutterBottom>
                        {plan.name}
                      </Typography>

                      {plan.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {plan.description}
                        </Typography>
                      )}

                      <Box sx={{ mb: 3 }}>
                        {plan.sale_price &&
                          parseFloat(plan.sale_price) < parseFloat(plan.price) && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 0.5,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Typography
                                variant="h6"
                                component="div"
                                sx={{
                                  textDecoration: 'line-through',
                                  color: 'text.disabled',
                                }}
                              >
                                {parseFloat(plan.price).toLocaleString('ru-RU', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}{' '}
                                ₽
                              </Typography>
                              {(() => {
                                const originalPrice = parseFloat(plan.price)
                                const salePrice = parseFloat(plan.sale_price)
                                const discountPercent = Math.round(
                                  ((originalPrice - salePrice) / originalPrice) * 100,
                                )
                                return (
                                  <Chip
                                    label={`-${discountPercent}%`}
                                    color="error"
                                    size="small"
                                    sx={{ fontWeight: 'bold' }}
                                  />
                                )
                              })()}
                            </Box>
                          )}
                        <Typography variant="h4" component="div" color="primary.main">
                          {plan.sale_price
                            ? parseFloat(plan.sale_price).toLocaleString('ru-RU', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })
                            : parseFloat(plan.price).toLocaleString('ru-RU', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{' '}
                          ₽
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {plan.duration}
                        </Typography>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      {plan.parameters && plan.parameters.length > 0 && (
                        <>
                          <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                            {t('subscription.parameters')}:
                          </Typography>
                          <Box component="ul" sx={{ m: 0, pl: 2 }}>
                            {plan.parameters.map((param, index) => (
                              <li key={index}>
                                <Typography variant="body2">
                                  {t(`subscription.parameters.${param.parameter}`, {
                                    defaultValue: param.parameter,
                                  })}
                                  :{' '}
                                  {param.value === 'true'
                                    ? t('common.yes')
                                    : param.value === 'false'
                                      ? t('common.no')
                                      : param.value}
                                </Typography>
                              </li>
                            ))}
                          </Box>
                        </>
                      )}

                      <Box sx={{ mt: 'auto', pt: 2 }}>
                        {currentSubscription?.is_active &&
                          currentSubscription?.expires_at &&
                          new Date(currentSubscription.expires_at) >= new Date() &&
                          plan.is_renewable !== false &&
                          !isActive && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={startAfterCurrentForPlanId === plan.id}
                                  onChange={(_, checked) =>
                                    setStartAfterCurrentForPlanId(checked ? plan.id : null)
                                  }
                                  size="small"
                                />
                              }
                              label={t('subscription.startAfterCurrent')}
                              sx={{ display: 'block', mb: 1 }}
                            />
                          )}
                        {isActive ? (
                          <Button variant="contained" fullWidth disabled>
                            {t('subscription.active')}
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={() => handleSubscribe(plan.id)}
                            disabled={isSubscribing || !plan.available || hasPendingSubscription}
                          >
                            {isSubscribing
                              ? t('common.loading')
                              : t('subscription.buyFromBalance', {
                                  price: formatMoney(
                                    plan.sale_price ?? plan.price,
                                    balanceCurrency,
                                  ),
                                })}
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              )
            })}
          </Box>
        )}

        {plans.length === 0 && !isLoading && !error && (
          <Alert severity="info">{t('subscription.noPlans')}</Alert>
        )}
      </Box>
    </ProfileLayout>
  )
}
